import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { apiClient } from '@/shared/api/apiClient'
import { EMC_PRODUCT_COMPLIANCE_FILES_QUERY_KEY } from '@/shared/hooks/api/emcComplianceFiles'

export const EMC_PRODUCT_COMPLIANCE_FILE_SLOT_QUERY_KEY =
  'emcProductComplianceFileSlot' as const

export type EmcProductComplianceFileEvidenceSlot =
  | 'DRAWINGS_SCHEMAS'
  | 'TEST_REPORT_ELECTROMAGNETIC_DISTURBANCE'
  | 'TEST_REPORT_ELECTROMAGNETIC_IMMUNITY'
  | 'DESCRIPTIONS_AND_EXPLANATIONS'

export type EmcComplianceFileAttachment = {
  id: string
  fileName: string
  contentType: string | null
  fileSizeKb: number | null
  uploadedAt: string
  uploadedByUserId: string | null
}

export type EmcComplianceFileSlotAttachmentsView = {
  files: EmcComplianceFileAttachment[]
}

function slotQueryKey(
  productId: string,
  slotKind: EmcProductComplianceFileEvidenceSlot
) {
  return [EMC_PRODUCT_COMPLIANCE_FILE_SLOT_QUERY_KEY, productId, slotKind] as const
}

export function useGetEmcComplianceFileSlot(
  productId: string | undefined,
  slotKind: EmcProductComplianceFileEvidenceSlot,
  options?: { enabled?: boolean }
) {
  return useQuery<EmcComplianceFileSlotAttachmentsView, Error>({
    queryKey: slotQueryKey(productId ?? '', slotKind),
    queryFn: async () => {
      const response = await apiClient.get<EmcComplianceFileSlotAttachmentsView>(
        `/v2/products/${productId}/emc/compliance-file/slots/${slotKind}`
      )
      return response.data
    },
    enabled:
      (options?.enabled ?? true) &&
      productId != null &&
      productId.length > 0,
  })
}

export function useUploadEmcComplianceFileSlot(
  productId: string,
  slotKind: EmcProductComplianceFileEvidenceSlot
) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (file: File) => {
      const formData = new FormData()
      formData.append('file', file, file.name)
      formData.append('fileName', file.name)
      const response = await apiClient.post<EmcComplianceFileSlotAttachmentsView>(
        `/v2/products/${productId}/emc/compliance-file/slots/${slotKind}`,
        formData,
        {
          headers: { 'Content-Type': 'multipart/form-data' },
        }
      )
      return response.data
    },
    onSuccess: (data) => {
      queryClient.setQueryData(slotQueryKey(productId, slotKind), data)
      void queryClient.invalidateQueries({
        queryKey: [EMC_PRODUCT_COMPLIANCE_FILES_QUERY_KEY, productId],
      })
    },
  })
}

export function useDeleteEmcComplianceFileSlotAttachment(
  productId: string,
  slotKind: EmcProductComplianceFileEvidenceSlot
) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (attachmentId: string) => {
      const response = await apiClient.delete<EmcComplianceFileSlotAttachmentsView>(
        `/v2/products/${productId}/emc/compliance-file/slots/${slotKind}/${attachmentId}`
      )
      return response.data
    },
    onSuccess: (data) => {
      queryClient.setQueryData(slotQueryKey(productId, slotKind), data)
      void queryClient.invalidateQueries({
        queryKey: [EMC_PRODUCT_COMPLIANCE_FILES_QUERY_KEY, productId],
      })
    },
  })
}

export function isEmcComplianceFileSlotFilled(
  slot: EmcComplianceFileSlotAttachmentsView | undefined
): boolean {
  return (slot?.files.length ?? 0) > 0
}
